import { z } from 'zod';
import * as t from 'Shared/model/basic';
import { contentKey } from 'Shared/model/Fields';
import { styleTag } from 'Shared/model/styleTag';

export const layoutTypes = z.enum(['slides','overlay','styles','link','vertical','text','grid','image','posters','buttons','popup','embed','visualWithInfo']);

export const layout:z.ZodType<any> = z.lazy(() => 		//FIXME any
	z.discriminatedUnion('layout', [
		slidesLayout,
		linkLayout,
		gridLayout,
		overlayLayout,
		stylesLayout,
		imageLayout,
		postersLayout,
		buttonsLayout,
		popupLayout,
		embedLayout
	])
);

/*
	A css length, 'full', or the fluid length the Amount control writes - see Shared/frontend/widgets/Amount.
	Same shape as venueSettings' fluidLength, which can't be imported here without a cycle.
*/
export const amount = z.union([
	z.string(),
	z.object({
		type: z.literal('fluid'),
		pageMin: z.number(),
		minValue: z.number(),
		pageMax: z.number(),
		maxValue: z.number(),
	})
]);

export const cropRatio = z.enum(['comp1x1','comp3x2','comp2x3']);

/* What the VerticalConstraint control writes */
export const heightConstraint = z.object({
	parent: z.boolean().optional(),
	child: z.boolean().optional(),
	height: amount.optional(),
	ratio: z.union([cropRatio, z.literal('custom')]).optional(),
	customRatio: z.number().optional()
});

export const alignment = z.object({
	side: z.enum(['center','left','right','top','bottom']),
	offset: amount.optional()
});

export const slidesLayout = z.object({
	layout: z.literal('slides'), 
	slides: z.array(z.any()),    //XXX any
	height: heightConstraint
});

export const overlayLayout = z.object({
	layout: z.literal('overlay'), 
	foreground: layout,
	background: layout,
	width: amount.optional(),
	height: heightConstraint,
	foregroundHeight: heightConstraint.optional(),
	vertical: alignment,
	horizontal: alignment
});

export const stylesLayout = z.object({
	layout: z.literal('styles'), 
	content: layout,
	styles: z.array(styleTag).optional(),
	removeStyles: z.array(styleTag.pick({category:true,type:true})).optional(),
	classes: z.array(z.string()).optional()
});

export const linkPosition = z.enum(['last','top','anchor']);

export const internalLink = z.object({
	type: z.literal('internal'),
	page: z.string(),
	position: linkPosition,
	anchor: z.string().optional(),
	openInSameWindow: z.boolean().optional()
});

export const externalLink = z.object({
	type: z.literal('external'),
	url: z.string().optional(),
	openInSameWindow: z.boolean().optional(),
	gDriveDownloadAsPDF: z.boolean().optional()
});

/* A link to one of the venue's menus, which are served at their own slug like pages are. */
export const menuLink = z.object({
	type: z.literal('menu'),
	menu: z.string(),
	position: linkPosition,
	anchor: z.string().optional(),
	openInSameWindow: z.boolean().optional()
});

export const linkTarget = z.discriminatedUnion('type',[internalLink,menuLink,externalLink]);

export const linkLayout = z.object({
	layout: z.literal('link'), 
//TODO divide up properly
	isAnchor: z.boolean().optional(),
	anchor: z.string().optional(),
	target: linkTarget.optional(),
	content: layout
});

export const verticalLayout = z.object({
	layout: z.literal('vertical'), 
	items: z.array(z.any())    //XXX any
});

export const textLayout = z.object({  //TODO separat into subtypes
	layout: z.literal('text'), 
	type: z.string(), 	//TODO more specific
	text: z.string(),
	useDbContent: z.boolean().optional(),  
	dbContent: contentKey.optional()
});

export const gridLayout = z.object({
	layout: z.literal('grid'), 
	items: z.array(z.any()),    //XXX any
	itemsPerRow: z.number().optional(),
	cellHeight: heightConstraint,
	fold: z.boolean().optional()
});

export const imageLayout = z.object({
	layout: z.literal('image'), 
	image: t.image.optional(),
	upload: z.object({ratio: cropRatio.optional()}),
	display: heightConstraint.extend({
		fit: z.enum(['cover','contain','scale-down','fill','none']).optional(),
		position: z.enum(['center','top','bottom','left','right']).optional(),
		size: t.imageFormats.keyof().optional()
	})
});

export const postersLayout = z.object({
	layout: z.literal('posters'), 
	type: z.enum(['music','nonMusic','events','pastEvents','specials','hours','emails','socials','address','phone','businessNumbers']).optional(),
	specialsType: z.string().optional(),
	carouselToScroll: z.number().optional()
});

export const button = z.object({
	text: z.string().optional(),
	target: linkTarget.optional()
});

export const buttonsLayout = z.object({
	layout: z.literal('buttons'), 
	isNav: z.boolean().optional(),
	sticky: z.boolean().optional(),
	buttons: z.array(button).optional()
});

export const popupLayout = z.object({
	layout: z.literal('popup'), 
	content: layout
});

export const embedLayout = z.object({
	layout: z.literal('embed'), 
	type: z.enum(['iframe','code']).optional(),
	height: heightConstraint.optional(),
	url: z.string().optional(),
	code: z.string().optional()
});

export const visualWithInfo = z.object({
	layout: z.literal('visualWithInfo'), 
	visualPosition: z.enum(['left','right']).optional(),
	visual: layout,
	upper: layout,
	lower: layout
});

export type LayoutTypes = z.infer<typeof layoutTypes>;
export type SlidesLayout = z.infer<typeof slidesLayout>;
export type GridLayout = z.infer<typeof gridLayout>;
export type OverlayLayout = z.infer<typeof overlayLayout>;
export type StylesLayout = z.infer<typeof stylesLayout>;
export type LinkLayout = z.infer<typeof linkLayout>;
export type LinkPosition = z.infer<typeof linkPosition>;
export type InternalLink = z.infer<typeof internalLink>;
export type ExternalLink = z.infer<typeof externalLink>;
export type MenuLink = z.infer<typeof menuLink>;
export type SiteLink = InternalLink | MenuLink;
export type LinkTarget = z.infer<typeof linkTarget>;
export type Button = z.infer<typeof button>;
export type VerticalLayout = z.infer<typeof verticalLayout>;
export type TextLayout = z.infer<typeof textLayout>;
export type ImageLayout = z.infer<typeof imageLayout>;
export type PostersLayout = z.infer<typeof postersLayout>;
export type ButtonsLayout = z.infer<typeof buttonsLayout>;
export type PopupLayout = z.infer<typeof popupLayout>;
export type EmbedLayout = z.infer<typeof embedLayout>;
export type Layout = z.infer<typeof layout>;
export type VisualWithInfoLayout = z.infer<typeof visualWithInfo>;
export type Amount = z.infer<typeof amount>;
export type CropRatio = z.infer<typeof cropRatio>;
export type HeightConstraint = z.infer<typeof heightConstraint>;
export type Alignment = z.infer<typeof alignment>;


export const home = z.object({
	_id: t.id(),
	siteId: t.id(),
	layout: z.array(layout),
});

export type HomeDoc = z.infer<typeof home>;

